// ビューアのメディア読み込み順制御で使う「src の保留」ヘルパー。
// 開いた瞬間に全スライドのメディアを同時に読むと帯域を奪い合い、今見ているコンテンツの表示が遅れるため、
// 順番が来るまでは src を入れず URL を data 属性に保留しておき、必要になった時点で src へ移す。
// 読み込み範囲から外れた動画は releaseMediaSrc で保留へ戻す。
const DEFERRED_SRC_KEY = 'storeeelDeferredSrc';
export const DEFERRED_SRC_SELECTOR = '[data-storeeel-deferred-src]';
// 読み込みが落ち着いたか判定できない端末（iOS の preload 無視等）でも先へ進めるための上限
const SETTLE_TIMEOUT_MS = 5000;

const isVideo = (el) => el?.tagName === 'VIDEO';

// src を入れずに URL だけ保留する（読み込み中の旧メディアがあれば中断して帯域を空ける）
export const deferMediaSrc = (mediaEl, url) => {
  if (!mediaEl || !url) return;
  mediaEl.dataset[DEFERRED_SRC_KEY] = url;
  const hadSrc = Boolean(mediaEl.getAttribute('src'));
  if (hadSrc) mediaEl.removeAttribute('src');
  if (!isVideo(mediaEl)) return;
  mediaEl.preload = 'none';
  mediaEl.autoplay = false;
  mediaEl.removeAttribute('autoplay');
  if (hadSrc) {
    try { mediaEl.load(); } catch { void 0; }
  }
};

// 読み込み範囲から外れた動画の src を保留へ戻し、取得を打ち切る。解放した場合は true を返す。
// pause しただけの動画は通信が開いたまま残り、溜まると後続の動画が一切届かなくなるため、範囲外は必ずこれで解放する。
// 画像は取得が終われば通信が残らないため対象外。
export const releaseMediaSrc = (mediaEl) => {
  if (!isVideo(mediaEl)) return false;
  const url = mediaEl.getAttribute('src');
  if (!url) return false;
  try { mediaEl.pause(); } catch { void 0; }
  deferMediaSrc(mediaEl, url);
  return true;
};

// root 配下の動画をすべて解放する（DOM から外す直前に使う。外すだけでは取得が止まらないことがある）
export const releaseMediaSrcIn = (rootEl) => {
  rootEl?.querySelectorAll?.('video').forEach((videoEl) => releaseMediaSrc(videoEl));
};

// 保留していた URL の保留だけを解除する（呼び出し側で別の src を入れる場合）
export const clearDeferredMediaSrc = (mediaEl) => {
  if (mediaEl?.dataset) delete mediaEl.dataset[DEFERRED_SRC_KEY];
};

// 保留中の URL を src に入れる。入れた場合は true を返す（動画の load() は呼び出し側で行う）
export const materializeDeferredMediaSrc = (mediaEl) => {
  const url = mediaEl?.dataset?.[DEFERRED_SRC_KEY];
  if (!url) return false;
  clearDeferredMediaSrc(mediaEl);
  if (mediaEl.getAttribute('src') === url) return false;
  if (isVideo(mediaEl)) mediaEl.preload = 'auto';
  mediaEl.src = url;
  return true;
};

// メディアの読み込みが落ち着く（動画: 再生しきれる量が溜まる・ブラウザが取得を止める / 画像: 読み込み完了、
// いずれも失敗含む）まで待つ
export const waitForMediaSettled = (mediaEl) => new Promise((resolve) => {
  if (!mediaEl || !mediaEl.getAttribute('src')) {
    resolve();
    return;
  }
  const video = isVideo(mediaEl);
  if (video ? mediaEl.readyState >= 4 : (mediaEl.complete && mediaEl.naturalWidth > 0)) {
    resolve();
    return;
  }
  let timerId = null;
  // emptied: 待っている間に releaseMediaSrc で解放された場合
  const events = video ? ['canplaythrough', 'suspend', 'error', 'emptied'] : ['load', 'error'];
  const done = (event) => {
    // suspend は取得前にも来ることがあるため、最初のフレームが出せる状態以降のみ「落ち着いた」とみなす
    if (event?.type === 'suspend' && mediaEl.readyState < 2) return;
    // emptied は src を入れた直後の load() でも来るため、src が外れた（解放された）ときだけ扱う
    if (event?.type === 'emptied' && mediaEl.getAttribute('src')) return;
    events.forEach((name) => mediaEl.removeEventListener(name, done));
    clearTimeout(timerId);
    resolve();
  };
  events.forEach((name) => mediaEl.addEventListener(name, done));
  timerId = setTimeout(done, SETTLE_TIMEOUT_MS);
});
